import { UitkCardAlohaContentSection, UitkCardAloha, UitkCardAlohaView } from "uitk-react-cards";
import { UitkHeading, UitkSubheading } from "uitk-react-text";
import { UitkFigure, UitkImage } from "uitk-react-images";

import { UitkIcon } from "uitk-react-icons";
import * as React from "react";
import { decorateEditorialHtml } from "components/flexComponents/Content/ContentUtilities";
import { Media } from "typings/microserviceModels/content-flex-module";
import { ThumborStore } from "stores/thumbor/ThumborStore";
import { inject } from "mobx-react";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { uitkFigureAspectRatio, imageRatio } from "../editorials/EditorialWithLink";
import { CTACarouselRegionContext } from "src/components/flexComponents/CTACarouselRegion/CTACarouselRegion";
import { SimpleContainerContext } from "src/components/flexComponents/SimpleContainer/SimpleContainer";

const HIGH_QUALITY_IMAGE_THUMBOR_SCORE = 100;
const LOW_QUALITY_IMAGE_THUMBOR_SCORE = 80;
const THUMBOR_WIDTH = 1040;
const THUMBOR_HEIGHT = 580;
/**
 * Editorial Item which will wrap text in a split column for desktop
 * and mobile pages as well as a card.
 */
export const RenderAbleContentItem = inject("thumbor")(
  ({
    id,
    text,
    title,
    subtitle,
    view,
    toggle,
    media,
    padding,
    boxType,
    thumbor,
    context,
    imageAspectRatio = "16-9",
  }: {
    id: string;
    text?: string;
    title?: string;
    subtitle?: string;
    view?: string;
    toggle?: string;
    media?: Media;
    padding?: string;
    boxType?: string;
    thumbor?: ThumborStore;
    context?: ExtendedContextStore;
    imageAspectRatio?: string;
  }) => {
    const isCollapseEnable = title && (toggle === "collapsed" || toggle === "expanded");
    const [toggleStatus, setToggle] = React.useState(toggle === "collapsed");
    const flipToggleStatus = (): void => {
      setToggle(!toggleStatus);
    };
    const isMobile = !!context?.deviceInformation?.mobile;
    const mediaUrl = media?.mediaUrl;
    let thumborizedImageUrl = mediaUrl;
    if (mediaUrl && thumbor) {
      const quality = isMobile ? LOW_QUALITY_IMAGE_THUMBOR_SCORE : HIGH_QUALITY_IMAGE_THUMBOR_SCORE;
      thumborizedImageUrl = thumbor.safelyGenerateThumborizedImageUrl(
        mediaUrl as string,
        THUMBOR_WIDTH,
        THUMBOR_HEIGHT,
        context,
        imageRatio(imageAspectRatio),
        true,
        quality
      );
    }
    const simpleContainerContext = React.useContext(SimpleContainerContext);
    const ctaCarouselContext = React.useContext(CTACarouselRegionContext);
    const shouldRenderBorder = simpleContainerContext.modulesHaveBorder || ctaCarouselContext.hasBorder;

    return (
      <UitkCardAloha className={boxType === "blossom-plain" ? "noBackground" : ""} border={shouldRenderBorder}>
        {thumborizedImageUrl && (
          <UitkCardAlohaView>
            <UitkFigure ratio={uitkFigureAspectRatio(imageAspectRatio)}>
              <UitkImage src={thumborizedImageUrl} alt={media?.mediaAlt || ""} />
            </UitkFigure>
          </UitkCardAlohaView>
        )}
        {isCollapseEnable && (
          <div role="button" aria-expanded={!toggleStatus} className="header" onClick={flipToggleStatus}>
            <UitkCardAlohaContentSection
              padded={padding !== "none"}
              className={`uitk-flex all-x-padding-zero uitk-flex-align-items-center all-y-padding-four ${
                toggleStatus && "allBorderZero"
              }`}
            >
              <UitkHeading level={2} size={600}>
                {title}
              </UitkHeading>
              <UitkSubheading level={3}>{subtitle}</UitkSubheading>
              <UitkIcon name={`expand_${toggleStatus ? "more" : "less"}`} />
            </UitkCardAlohaContentSection>
          </div>
        )}
        <div
          data-testid="editorial-content"
          className={`editorialContent ${isCollapseEnable && toggleStatus ? "is-visually-hidden" : ""}`}
        >
          <UitkCardAlohaContentSection padded={padding !== "none"} className="uitk-grid">
            <div className="uitk-cell s-cell-1-1 m-cell-1-1 l-cell-1-1 xl-cell-1-1">
              <div className="uitk-type-paragraph-300" data-fm-content={id} id={id}>
                {title && !isCollapseEnable && (
                  <UitkHeading level={2} size={600}>
                    {title}
                  </UitkHeading>
                )}
                {subtitle && !isCollapseEnable && <UitkSubheading level={4}>{subtitle}</UitkSubheading>}
                {text && (
                  <div
                    className={`all-t-padding-two uitk-type-list-300 ${
                      view === "single-column" ? "singleColumn" : "editorialTextColumn"
                    }`}
                    dangerouslySetInnerHTML={decorateEditorialHtml(text)}
                  />
                )}
              </div>
            </div>
          </UitkCardAlohaContentSection>
        </div>
      </UitkCardAloha>
    );
  }
);
